import { useEffect, useRef, useState } from "react";
import { FileVideo } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Loads a Drive video thumbnail through the backend proxy and shows it.
 * Falls back to a video icon when the file has no thumbnail or the fetch fails.
 * Uses object URLs so the JWT stays in the fetch header, not in the img src.
 */
export default function DriveThumbnail({ fileId, hasThumbnail, name, className = "", size = 320 }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    setUrl(null);
    setFailed(false);
    if (!fileId || hasThumbnail === false) return;
    (async () => {
      try {
        const resp = await api.get(`/drive/thumbnail/${encodeURIComponent(fileId)}`, {
          params: { size },
          responseType: "blob",
        });
        if (cancelledRef.current) return;
        const objUrl = URL.createObjectURL(resp.data);
        setUrl(objUrl);
      } catch {
        if (!cancelledRef.current) setFailed(true);
      }
    })();
    return () => {
      cancelledRef.current = true;
    };
  }, [fileId, hasThumbnail, size]);

  useEffect(() => {
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [url]);

  if (!fileId || hasThumbnail === false || failed) {
    return (
      <div
        className={`shrink-0 grid place-items-center rounded-md bg-zinc-100 text-slate-400 ${className}`}
        aria-label="No thumbnail"
      >
        <FileVideo className="w-5 h-5" />
      </div>
    );
  }

  return (
    <div className={`shrink-0 relative overflow-hidden rounded-md bg-zinc-100 ${className}`}>
      {url ? (
        <img
          src={url}
          alt={name || "Video thumbnail"}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 grid place-items-center text-slate-300">
          <FileVideo className="w-5 h-5" />
        </div>
      )}
    </div>
  );
}
